import { Dispatch, FC, SetStateAction } from "react";
import { CattleFeed } from "../../../../models/Intervention/CattleIntervention";
import { FormType, ListEntry } from "../../common";
import { AnimalType, MasterDataOption, Maybe } from "../../../../../../graphql/types";
import { DsmGrid } from "@dsm-dcs/design-system-react";
import { useIntl } from "../../../../../../_metronic/i18n/customUseIntl";
import { DairyIntervention } from "../../../../models/Intervention";
import { ArrayField, useFormContext } from "react-hook-form";
import { processAndStageStylesV2 } from "../../../../../../_metronic/layout";
import { CSSClassesList } from "../../../../helpers/helperTypes";
import { BaselineFeedWithOrigin } from "../../../../models/Baseline";
import ReactHookDsmInput from "../../../../../modules/Helpers/ReactHookDsmInput2";
import ReactHookDsmSelect from "../../../../../modules/Helpers/ReactHookDsmSelect2";
import SustellInterventionSingleIngredientFeedComponent from "../../../../../modules/Farms/Intervention/SustellInterventionSingleIngredientFeedComponent";
import SustellSingleIngredientFeed from "../../../../../modules/Farms/Baseline/SustellSingleIngredientFeeds_v2";
import { Origin } from "../types";
import { formatLabelName } from "../../../../utils/string-utils";
import EditModeButtons from "../../../helpers/EditModeButtons";
import { Spacer } from "../../../helpers/Spacer";
import { isFieldFilled } from "../CommonDataParts/InterventionGenericInfoDialog";
import RowSelectWithMetricsV2 from "../../../../../modules/Farms/Intervention/RowSelectWithMetricsV2";
import RowTextFieldWithMetrics from "../../../../../modules/Farms/Intervention/RowControlledTextFieldWithMetrics2";

interface DairyV2FeedSingleIngredientComponentProps {
  singleIngredients: Maybe<Array<MasterDataOption>>;
  origins: Maybe<Array<Origin>>;
  formType: FormType,
  stageIndex: number,
  fieldItemPrefix: string,
  currentStageData: CattleFeed,
  isSingleIngEdit: boolean,
  setIsSingleIngEdit: Dispatch<SetStateAction<boolean>>;
  ingredientsAdditions: Partial<ArrayField<Record<string, any>, "keyId">>[];
  appendIngredientsAdditions: (value: Partial<Record<string, any>> | Partial<Record<string, any>>[], shouldFocus?: boolean) => void;
  removeIngredientsAdditions: (index?: number | number[]) => void;
  resetEdit: () => void;
};

const DairyV2FeedSingleIngredientComponent: FC<DairyV2FeedSingleIngredientComponentProps> = ({
  singleIngredients,
  origins,
  formType = FormType.Add,
  stageIndex,
  fieldItemPrefix,
  currentStageData,
  isSingleIngEdit,
  setIsSingleIngEdit,
  ingredientsAdditions,
  appendIngredientsAdditions,
  removeIngredientsAdditions,
  resetEdit,
}) => {
  const intl = useIntl();
  const classes = processAndStageStylesV2() as CSSClassesList;
  const fc = useFormContext<DairyIntervention>();

  const animalType = (fc.getValues("animalType") as AnimalType) ?? undefined;
  const barnOutputMassUnit = "kg DM";
  const singleIngOrigins: ListEntry[] = origins as ListEntry[];

  const renderSingleIngredientUnit = (item: BaselineFeedWithOrigin, i: number) => {
    return (
      <>
        {!isSingleIngEdit ? (
          <DsmGrid className={classes.dsmGridTwoColumnsIngredientInterventionComponent}>
            {isFieldFilled(fc.getValues(`${fieldItemPrefix}.singleIngredients[${i}].kgPerAnimal`)) ? (
              <div>
                <RowTextFieldWithMetrics
                  name={`${fieldItemPrefix}.singleIngredients[${i}].kgPerAnimal`}
                  tooltip={intl.formatMessage({ id: "INTERVENTION.FORM.BARN.RATION.SINGLE_INGREDIENTS.TOOLTIP" })}
                  label={formatLabelName(item.feedType)}
                  metricUnit={barnOutputMassUnit}
                  baseline={item.kgPerAnimal}
                  displayTypeSelect={false}
                  type="number"
                  disabled
                />
                <Spacer />
              </div>
            ) : (
              <ReactHookDsmInput
                name={`${fieldItemPrefix}.singleIngredients[${i}].originalKgPerAnimal`}
                tooltip={intl.formatMessage({ id: "INTERVENTION.FORM.BARN.RATION.SINGLE_INGREDIENTS.TOOLTIP" })}
                label={formatLabelName(item.feedType)}
                defaultValue={item.kgPerAnimal ?? ''}
                adornment={barnOutputMassUnit}
                type="number"
                disabled
              />
            )}
            {isFieldFilled(fc.getValues(`${fieldItemPrefix}.singleIngredients[${i}].origin`)) ? (
              <div>
                <RowSelectWithMetricsV2
                  name={`${fieldItemPrefix}.singleIngredients[${i}].origin`}
                  label={intl.formatMessage({ id: "SUSTELL.STAGE.FEED.SINGLE_INGREDIENT.ORIGIN" })}
                  baseline={item.origin}
                  options={singleIngOrigins}
                  originalLabel={!!(
                    fc.getValues(`${fieldItemPrefix}.singleIngredients[${i}].origin`) &&
                    fc.getValues(`${fieldItemPrefix}.singleIngredients[${i}].origin`) !==
                    item.origin
                  )}
                  useStyle={false}
                  disabled
                />
                <Spacer />
              </div>
            ) : (
              <ReactHookDsmSelect
                name={`${fieldItemPrefix}.singleIngredients[${i}].originalOrigin`}
                label={intl.formatMessage({ id: "SUSTELL.STAGE.FEED.SINGLE_INGREDIENT.ORIGIN" })}
                defaultValue={item.origin}
                options={singleIngOrigins}
                adornment=""
                disabled
              />
            )}
          </DsmGrid>
        ) : (
          <SustellInterventionSingleIngredientFeedComponent
            formType={!isSingleIngEdit ? FormType.View : formType}
            singleIngredient={item}
            ingredientIndex={i}
            fieldItemPrefix={fieldItemPrefix}
            availableOrigins={singleIngOrigins}
            displayTypeSelect={isSingleIngEdit}
          />
        )}
      </>
    )
  }

  return (
    <DsmGrid className={classes.dsmGridOneColumnWithBorder}>
      <Spacer />
      {(currentStageData?.singleIngredients || [])
        ?.filter((item) => !!item.feedType && !!item.origin)
        ?.map((singleIngredientItem: BaselineFeedWithOrigin, i: number) =>
          (renderSingleIngredientUnit(singleIngredientItem, i))
        )}
      {(isSingleIngEdit ||
        (!isSingleIngEdit && ingredientsAdditions?.length !== 0)) && (
          <SustellSingleIngredientFeed
            key="singleIngredients"
            label={intl.formatMessage({
              id: "SUSTELL.STAGE.FEED.SINGLE_INGREDIENT",
            })}
            barnItemIndex={stageIndex}
            availableFeedItems={singleIngredients}
            availableOrigins={origins}
            fieldItemPrefix={fieldItemPrefix}
            subFieldName="singleIngredientsAdditions"
            tooltip={
              <div key="feeds-form-dialog-singleIngredients">
                {intl.formatMessage(
                  {
                    id: "BASELINE.FORM.BARN.RATION.SINGLE_INGREDIENTS.TOOLTIP",
                  },
                  { br: <br /> }
                )}
              </div>
            }
            formState={isSingleIngEdit ? formType : FormType.View}
            feedData={ingredientsAdditions}
            fieldsAppenRemoveObject={{
              fields: ingredientsAdditions,
              append: appendIngredientsAdditions,
              remove: removeIngredientsAdditions,
            }}
            allowNewIngredients={isSingleIngEdit}
            animalType={animalType}
            isIntervention
          />
        )}
      {formType !== FormType.View &&
        <EditModeButtons
          isEditing={isSingleIngEdit}
          onEdit={() => setIsSingleIngEdit(true)}
          onCancel={resetEdit}
        />}
    </DsmGrid>
  )
}

export default DairyV2FeedSingleIngredientComponent;
